import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import { displayName } from "../utils/capabilities";
import BackButton from "../components/BackButton";

const relativeTime = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const SCOPE_LABEL = {
  building: "Building",
  job: "Job",
  account: "Account",
  direct: "Direct",
};

/**
 * Inbox of cross-org message threads (contractor ↔ facility). Lists threads
 * newest-first by lastMessageAt; clicking opens the thread page. "New
 * Message" starts a thread on a chosen building/job/account.
 */
export default function MessagesPage({
  messageThreadsRaw = [],
  customers = [],
  jobsRaw = [],
  orgId,
  user,
  threadLastRead = {},
  actions,
  onOpenThread,
  onReload,
  onBack,
}) {
  // Same-org user directory for the Direct-message picker. Subscribes to
  // every user in our org (excluding ourselves and deleted accounts).
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(
      q,
      (snap) =>
        setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return unsub;
  }, [orgId]);

  // Cross-org user directory — partner orgs (everyone in any memberOrgs of
  // accounts/buildings we're linked with). Read from publicUserDirectory
  // (sanitized: name + email + role + jobTitle only, no profile HR data).
  // Stable key for the effect dep — array identity changes each render.
  const partnerOrgIdsKey = Array.from(
    new Set(
      customers
        .flatMap((c) => [
          ...(c.memberOrgs || []),
          ...(c.locations || []).flatMap((b) => b.memberOrgs || []),
        ])
        .filter((o) => o && o !== orgId)
    )
  )
    .sort()
    .join(",");
  const [partnerUsers, setPartnerUsers] = useState([]);
  useEffect(() => {
    const ids = partnerOrgIdsKey ? partnerOrgIdsKey.split(",") : [];
    if (ids.length === 0) {
      setPartnerUsers([]);
      return;
    }
    // Firestore "in" caps at 30; chunk if you ever exceed.
    const chunk = ids.slice(0, 30);
    const q = query(
      collection(db, "publicUserDirectory"),
      where("orgId", "in", chunk)
    );
    const unsub = onSnapshot(
      q,
      (snap) =>
        setPartnerUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn("partner user directory:", err.message)
    );
    return unsub;
  }, [partnerOrgIdsKey]);
  const [search, setSearch] = useState("");
  const [showCompose, setShowCompose] = useState(false);
  // Two-stage selection: select mode hides checkboxes for a clean
  // default list, then exposes them when entered. Same pattern as
  // iOS Mail / Gmail.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected({});
  };
  const [scope, setScope] = useState("direct");
  const [scopeId, setScopeId] = useState("");
  const [directRecipientUid, setDirectRecipientUid] = useState("");
  // Typeable DM picker: the input is freely typed; the list below filters
  // by query. When a row is clicked we set both the uid and the typed text
  // to that person's display name.
  const [directPickerQuery, setDirectPickerQuery] = useState("");
  const [directPickerOpen, setDirectPickerOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  // Look up an existing direct thread between me and the recipient so we
  // don't spawn duplicates each time.
  const findExistingDirect = (otherUid) => {
    if (!user?.uid || !otherUid) return null;
    return messageThreadsRaw.find((t) => {
      if (t.scope !== "direct") return false;
      const p = t.participants || [];
      return (
        p.length === 2 && p.includes(user.uid) && p.includes(otherUid)
      );
    });
  };

  // Pool of entities the user can scope a thread to: only those whose
  // memberOrgs contains a partner org (i.e. the building/account has at
  // least one other org besides ours). Jobs are filtered to those whose
  // building has a partner.
  const accountsWithPartner = customers.filter(
    (c) => (c.memberOrgs || []).some((o) => o && o !== orgId)
  );
  const buildingsWithPartner = customers.flatMap((c) =>
    (c.locations || [])
      .filter((b) => (b.memberOrgs || []).some((o) => o && o !== orgId))
      .map((b) => ({ ...b, customer: c }))
  );
  const jobsWithPartner = jobsRaw.filter((j) => {
    const b = buildingsWithPartner.find((x) => x.id === j.locationId);
    return !!b;
  });

  const memberOrgsFor = (s, id) => {
    if (s === "account") {
      const a = customers.find((c) => c.id === id);
      return a?.memberOrgs || [];
    }
    if (s === "building") {
      const b = buildingsWithPartner.find((x) => x.id === id);
      return b?.memberOrgs || [];
    }
    if (s === "job") {
      const j = jobsRaw.find((x) => x.id === id);
      const b = j ? buildingsWithPartner.find((x) => x.id === j.locationId) : null;
      return b?.memberOrgs || [];
    }
    return [];
  };

  const scopeLabelFor = (s, id) => {
    if (s === "account") return customers.find((c) => c.id === id)?.name || "";
    if (s === "building") {
      const b = buildingsWithPartner.find((x) => x.id === id);
      return b?.name || "";
    }
    if (s === "job") {
      const j = jobsRaw.find((x) => x.id === id);
      return j ? (j.jobNumber ? `${j.jobNumber} — ${j.title || ""}` : j.title || "") : "";
    }
    return "";
  };

  const submitNew = async () => {
    if (!body.trim()) return;
    if (scope === "direct") {
      if (!directRecipientUid || !user?.uid) return;
      // Reuse the existing 1:1 thread with this person if there is one.
      const existing = findExistingDirect(directRecipientUid);
      if (existing) {
        await actions.sendMessage(existing.id, body);
      } else {
        const me = orgUsers.find((u) => u.id === user.uid);
        // The recipient may be in our org (orgUsers) or a partner org
        // (partnerUsers). Try both lookups for display names + email.
        const otherSame = orgUsers.find((u) => u.id === directRecipientUid);
        const otherPartner = partnerUsers.find(
          (u) => u.id === directRecipientUid
        );
        const otherName = otherSame
          ? displayName(otherSame.profile, otherSame.email)
          : otherPartner?.name || otherPartner?.email || "Unknown";
        const otherEmail = otherSame
          ? otherSame.email || ""
          : otherPartner?.email || "";
        await actions.startMessageThread({
          scope: "direct",
          memberOrgs: [],
          participants: [user.uid, directRecipientUid],
          participantNames: [
            displayName(me?.profile, me?.email),
            otherName,
          ],
          participantEmails: [me?.email || "", otherEmail],
          subject,
          body,
        });
      }
      setSending(false);
      setShowCompose(false);
      setSubject("");
      setBody("");
      setDirectRecipientUid("");
      setDirectPickerQuery("");
      setDirectPickerOpen(false);
      return;
    }
    const memberOrgs = memberOrgsFor(scope, scopeId);
    if (!scopeId || memberOrgs.length < 2) return;
    setSending(true);
    await actions.startMessageThread({
      scope,
      scopeId,
      scopeLabel: scopeLabelFor(scope, scopeId),
      memberOrgs,
      subject,
      body,
    });
    setSending(false);
    setShowCompose(false);
    setSubject("");
    setBody("");
    setScopeId("");
  };

  const scopeOptions = () => {
    if (scope === "account")
      return accountsWithPartner.map((c) => ({ id: c.id, label: c.name }));
    if (scope === "building")
      return buildingsWithPartner.map((b) => ({
        id: b.id,
        label: `${b.name}${b.customer ? ` (${b.customer.name})` : ""}`,
      }));
    if (scope === "job")
      return jobsWithPartner.map((j) => ({
        id: j.id,
        label: j.jobNumber ? `${j.jobNumber} — ${j.title || ""}` : j.title || j.id,
      }));
    return [];
  };

  const term = search.trim().toLowerCase();
  const threads = [...messageThreadsRaw]
    .filter((t) => {
      if (!term) return true;
      return (
        (t.subject || "").toLowerCase().includes(term) ||
        (t.scopeLabel || "").toLowerCase().includes(term) ||
        (t.lastMessageBody || "").toLowerCase().includes(term)
      );
    })
    .sort((a, b) =>
      (b.lastMessageAt || "").localeCompare(a.lastMessageAt || "")
    );

  // Quick lookups for displaying scope labels if scopeLabel wasn't stored.
  const buildingName = (id) => {
    for (const c of customers) {
      const b = (c.locations || []).find((x) => x.id === id);
      if (b) return b.name;
    }
    return "Building";
  };
  const jobLabel = (id) => {
    const j = jobsRaw.find((x) => x.id === id);
    if (!j) return "Job";
    return j.jobNumber ? `${j.jobNumber} — ${j.title || ""}` : j.title || "Job";
  };
  const accountName = (id) => customers.find((c) => c.id === id)?.name || "Account";

  const scopeDisplay = (t) => {
    if (t.scope === "direct") {
      const names = t.participantNames || [];
      const emails = t.participantEmails || [];
      const idx =
        (t.participants || []).findIndex((u) => u !== user?.uid);
      if (idx >= 0) {
        return names[idx] || emails[idx] || "Direct message";
      }
      return "Direct message";
    }
    if (t.scopeLabel) return t.scopeLabel;
    if (t.scope === "building") return buildingName(t.scopeId);
    if (t.scope === "job") return jobLabel(t.scopeId);
    if (t.scope === "account") return accountName(t.scopeId);
    return "";
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Messages</h2>

      <button
        style={styles.button}
        onClick={() => setShowCompose((s) => !s)}
      >
        {showCompose ? "Cancel" : "+ New Message"}
      </button>
      {onReload && (
        <button
          style={styles.button}
          onClick={onReload}
          title="Reconnect if threads aren't updating"
          aria-label="Reload"
        >
          ↻
        </button>
      )}
      <br />
      <div style={{ marginTop: "8px" }} />

      {showCompose && (
        <div style={styles.card}>
          <select
            value={scope}
            onChange={(e) => {
              setScope(e.target.value);
              setScopeId("");
              setDirectRecipientUid("");
            }}
          >
            <option value="direct">
              Direct message (you + one teammate or partner)
            </option>
            <option value="building">
              Building (visible to everyone in linked orgs)
            </option>
          </select>
          <br />
          {scope === "direct" ? (
            <>
              {(() => {
                // Build the full candidate list — my org + partner orgs,
                // each tagged with the company name and a search string.
                const myOrgCompany = "My team";
                const myOrgOptions = orgUsers
                  .filter(
                    (u) => !u.deleted && !u.disabled && u.id !== user?.uid
                  )
                  .map((u) => ({
                    uid: u.id,
                    name: displayName(u.profile, u.email),
                    email: u.email || "",
                    role: u.role || "",
                    company: myOrgCompany,
                  }));
                // Look up partner org company names — prefer the new
                // orgName field on the directory entry; fall back to
                // linkedOrgInfo from any linked account/building; fall back
                // to a truncated orgId.
                const lookupCompany = (entry) => {
                  if (entry.orgName) return entry.orgName;
                  for (const c of customers) {
                    if (c.linkedOrgInfo?.[entry.orgId]?.name) {
                      return c.linkedOrgInfo[entry.orgId].name;
                    }
                    for (const b of c.locations || []) {
                      if (b.linkedOrgInfo?.[entry.orgId]?.name) {
                        return b.linkedOrgInfo[entry.orgId].name;
                      }
                    }
                  }
                  return entry.orgId
                    ? `Org ${entry.orgId.slice(0, 6)}`
                    : "Partner";
                };
                const partnerOptions = partnerUsers
                  .filter((u) => !u.deleted && !u.disabled && u.orgId)
                  .map((u) => ({
                    uid: u.id,
                    name: u.name || u.email || "Unknown",
                    email: u.email || "",
                    role: u.role || "",
                    company: lookupCompany(u),
                  }));
                const allOptions = [...myOrgOptions, ...partnerOptions];
                const q = directPickerQuery.trim().toLowerCase();
                const filtered = q
                  ? allOptions.filter(
                      (o) =>
                        o.name.toLowerCase().includes(q) ||
                        o.email.toLowerCase().includes(q) ||
                        o.company.toLowerCase().includes(q)
                    )
                  : allOptions;
                filtered.sort((a, b) => {
                  // "My team" first, then alphabetical by company, then name.
                  if (a.company === myOrgCompany && b.company !== myOrgCompany)
                    return -1;
                  if (b.company === myOrgCompany && a.company !== myOrgCompany)
                    return 1;
                  const c = a.company.localeCompare(b.company);
                  if (c !== 0) return c;
                  return a.name.localeCompare(b.name);
                });
                return (
                  <div style={{ position: "relative", display: "inline-block", width: "320px" }}>
                    <input
                      style={{ ...styles.input, width: "100%" }}
                      placeholder="Type a name or company..."
                      value={directPickerQuery}
                      onChange={(e) => {
                        setDirectPickerQuery(e.target.value);
                        setDirectPickerOpen(true);
                        setDirectRecipientUid("");
                      }}
                      onFocus={() => setDirectPickerOpen(true)}
                      // delay close so click on a list item registers first
                      onBlur={() =>
                        setTimeout(() => setDirectPickerOpen(false), 150)
                      }
                    />
                    {directPickerOpen && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          maxHeight: "260px",
                          overflowY: "auto",
                          background: "white",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                          zIndex: 100,
                        }}
                      >
                        {filtered.length === 0 ? (
                          <div style={{ padding: "8px", fontSize: "12px", color: "#666" }}>
                            No match.
                          </div>
                        ) : (
                          filtered.map((o) => (
                            <div
                              key={o.uid}
                              onMouseDown={(e) => {
                                // mousedown beats blur so the value sticks
                                e.preventDefault();
                                setDirectRecipientUid(o.uid);
                                setDirectPickerQuery(
                                  `${o.name} · ${o.company}`
                                );
                                setDirectPickerOpen(false);
                              }}
                              style={{
                                padding: "8px 10px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f0f0f0",
                                fontSize: "13px",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f4f8ff")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <strong>{o.name}</strong>
                              <span style={{ color: "#666" }}>
                                {" · "}
                                {o.company}
                              </span>
                              {o.role && (
                                <span style={{ color: "#888", fontSize: "11px" }}>
                                  {" — "}
                                  {o.role.replace(/_/g, " ")}
                                </span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <>
              <label style={{ fontSize: "12px" }}>
                {SCOPE_LABEL[scope] || scope}:{" "}
              </label>
              <select
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
              >
                <option value="">Select...</option>
                {scopeOptions().map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              {scopeId && memberOrgsFor(scope, scopeId).length < 2 && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#a33",
                    marginTop: "4px",
                  }}
                >
                  No partner org is linked here — share via code first.
                </p>
              )}
            </>
          )}
          <input
            style={styles.input}
            placeholder="Subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <textarea
            style={styles.textarea}
            placeholder="Type your message..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
          />
          <button
            style={styles.button}
            onClick={submitNew}
            disabled={
              sending ||
              !body.trim() ||
              (scope === "direct"
                ? !directRecipientUid
                : !scopeId || memberOrgsFor(scope, scopeId).length < 2)
            }
          >
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      )}

      <input
        style={styles.input}
        placeholder="Search threads..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {threads.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>No messages yet.</p>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              margin: "8px 0 6px",
              fontSize: "13px",
              flexWrap: "wrap",
            }}
          >
            {!selectMode ? (
              <button
                style={{ ...styles.button, margin: 0 }}
                onClick={() => setSelectMode(true)}
                title="Enter selection mode"
              >
                ✓ Select
              </button>
            ) : (
              <>
                <label style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={
                      threads.length > 0 &&
                      threads.every((t) => selected[t.id])
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const next = {};
                        threads.forEach((t) => (next[t.id] = true));
                        setSelected(next);
                      } else {
                        setSelected({});
                      }
                    }}
                  />{" "}
                  Select All
                </label>
                {(() => {
                  const ids = Object.keys(selected).filter((k) => selected[k]);
                  return (
                    <>
                      <span style={{ color: "#666" }}>{ids.length} selected</span>
                      <button
                        style={{
                          ...styles.button,
                          margin: 0,
                          background: ids.length ? "#fee" : "#f3f4f6",
                          color: ids.length ? "#a00" : "#999",
                        }}
                        disabled={bulkBusy || ids.length === 0}
                        onClick={async () => {
                          if (
                            !window.confirm(
                              `Delete ${ids.length} thread(s)? This removes them for everyone in the conversation and cannot be undone.`
                            )
                          )
                            return;
                          setBulkBusy(true);
                          try {
                            for (const id of ids) {
                              await actions?.deleteMessageThread?.(id);
                            }
                            exitSelectMode();
                          } finally {
                            setBulkBusy(false);
                          }
                        }}
                      >
                        {bulkBusy
                          ? "Deleting..."
                          : `Delete Selected${ids.length ? ` (${ids.length})` : ""}`}
                      </button>
                      <button
                        style={{ ...styles.button, margin: 0 }}
                        disabled={bulkBusy}
                        onClick={exitSelectMode}
                      >
                        Done
                      </button>
                    </>
                  );
                })()}
              </>
            )}
          </div>
          {threads.map((t) => {
          // Highlight if the last sender wasn't me AND I haven't opened the
          // thread since that message arrived. Per-user read state lives on
          // users/{uid}.threadLastRead. Falls back to org check for legacy
          // threads.
          const fromOther = t.lastMessageByUid
            ? !!user?.uid && t.lastMessageByUid !== user.uid
            : t.lastMessageByOrg && t.lastMessageByOrg !== orgId;
          const myLastRead = threadLastRead[t.id] || "";
          const unread =
            fromOther && (t.lastMessageAt || "") > myLastRead;
          return (
            <div
              key={t.id}
              style={{
                ...styles.card,
                cursor: "pointer",
                background: unread ? "#f4f8ff" : undefined,
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
              onClick={() => {
                actions?.markThreadRead?.(t.id);
                onOpenThread(t);
              }}
            >
              {selectMode && (
                <input
                  type="checkbox"
                  checked={!!selected[t.id]}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    setSelected((prev) => {
                      const next = { ...prev };
                      if (e.target.checked) next[t.id] = true;
                      else delete next[t.id];
                      return next;
                    })
                  }
                  style={{ marginTop: "3px" }}
                />
              )}
              <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <strong style={{ fontSize: "13px" }}>
                  {t.subject || "(no subject)"}
                </strong>
                <span style={{ fontSize: "11px", color: "#666" }}>
                  {relativeTime(t.lastMessageAt)}
                </span>
              </div>
              <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>
                <span
                  style={{
                    background: "#e0e0e0",
                    borderRadius: "4px",
                    padding: "1px 6px",
                    marginRight: "6px",
                  }}
                >
                  {SCOPE_LABEL[t.scope] || t.scope}
                </span>
                {scopeDisplay(t)}
              </div>
              {t.lastMessageBody && (
                <div style={{ fontSize: "12px", color: "#444", marginTop: "4px" }}>
                  <strong>
                    {t.lastMessageByName || t.lastMessageByEmail || "—"}:
                  </strong>{" "}
                  {t.lastMessageBody}
                </div>
              )}
              </div>
            </div>
          );
        })}
        </>
      )}
    </>
  );
}
